/* eslint-disable max-lines */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Pencil } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import { MemberPortalEmailChange, useMemberPortalEmailChange } from './MemberPortalEmailChange'
import { MemberPortalProfilePhoto } from './MemberPortalProfilePhoto'
import { MemberPortalChapters } from './MemberPortalChapters'
import { MemberPortalPostalFields } from './MemberPortalPostalFields'
import {
  editableCustomFields,
  profileValue,
  setCustomField,
  toDraft,
} from './MemberPortalProfileHelpers'
import { Link } from 'react-router-dom'

type ProfileField = {
  id?: number
  label: string
  value: string | boolean
  field_access: 'member_edit' | 'view_only'
  type: string
}
export type MemberPortalProfile = {
  name: string
  email: string
  pending_email_change?: { email: string; requested_at: string }
  phone?: string
  address_line1?: string
  address_line2?: string
  city?: string
  region?: string
  postal_code?: string
  country_code?: string
  custom_fields: ProfileField[]
  member_directory_slug?: string
  profile_photo?: { filename: string; thumbnail_url: string }
  chapters?: {
    chapters: Array<{ id: number; name: string }>
    available_chapters: Array<{ id: number; name: string }>
  }
}
type MemberPortalProfileResponse = { profile: MemberPortalProfile }
type ProfileSection = 'contact' | 'email' | 'custom_fields'
export type ProfileDraft = {
  name: string
  phone: string
  addressLine1: string
  addressLine2: string
  city: string
  region: string
  postalCode: string
  countryCode: string
  customFields: Record<string, string | boolean>
}
type ProfileErrors = Record<string, string[]>

export function MemberPortalProfile() {
  const queryClient = useQueryClient()
  const profile = useQuery<MemberPortalProfileResponse>({
    queryKey: ['member-portal-profile'],
    queryFn: () => getJSON('/api/v1/member_portal/profile'),
    retry: false,
  })
  const [editing, setEditing] = useState<ProfileSection | null>(null)
  const [draft, setDraft] = useState<ProfileDraft | null>(null)
  const [errors, setErrors] = useState<ProfileErrors>({})
  const current = profile.data?.profile
  const closeEmailEditor = () => setEditing(null)
  const emailFlow = useMemberPortalEmailChange(
    current ?? { email: '' },
    queryClient,
    closeEmailEditor,
  )
  const update = useMutation({
    mutationFn: (section: ProfileSection) =>
      patchJSON<MemberPortalProfileResponse>('/api/v1/member_portal/profile', {
        profile:
          section === 'contact'
            ? {
                name: draft?.name,
                phone: draft?.phone,
                address_line1: draft?.addressLine1,
                address_line2: draft?.addressLine2,
                city: draft?.city,
                region: draft?.region,
                postal_code: draft?.postalCode,
                country_code: draft?.countryCode,
              }
            : {
                custom_field_values: editableCustomFields(
                  current,
                  (draft ?? (current ? toDraft(current) : null))?.customFields ?? {},
                ),
              },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['member-portal-profile'], data)
      setEditing(null)
      setDraft(null)
      setErrors({})
    },
    onError: (error: ApiError) => {
      const body = error.body as { errors?: ProfileErrors } | undefined
      setErrors(body?.errors ?? {})
    },
  })
  if (profile.isLoading)
    return <p className="mt-6 text-body-sm text-muted-foreground">Loading profile...</p>
  if (!current)
    return <Alert className="mt-6">We couldn&apos;t load your profile. Please try again.</Alert>
  const workingDraft = draft ?? toDraft(current)
  const beginEditing = (section: ProfileSection) => {
    if (section === 'email') emailFlow.begin()
    else setDraft(toDraft(current))
    setErrors({})
    setEditing(section)
  }
  const cancelEditing = () => {
    setDraft(toDraft(current))
    setErrors({})
    setEditing(null)
    emailFlow.reset()
  }
  const save = () => {
    if (editing) update.mutate(editing)
  }
  return (
    <div className="mt-6 space-y-3">
      {current.member_directory_slug && (
        <div className="flex justify-end">
          <Button asChild type="button" variant="outline" size="sm">
            <Link to="/portal/profile/directory">Directory listing</Link>
          </Button>
        </div>
      )}
      <Card className="overflow-hidden">
        <MemberPortalProfilePhoto photo={current.profile_photo} />
        <ContactSection
          current={current}
          draft={workingDraft}
          editing={editing === 'contact'}
          errors={errors}
          onDraft={setDraft}
          onEdit={() => beginEditing('contact')}
          onSave={save}
          onCancel={cancelEditing}
          pending={update.isPending}
        />
        <MemberPortalEmailChange
          current={current}
          editing={editing === 'email'}
          flow={emailFlow}
          onEdit={() => beginEditing('email')}
          onCancel={cancelEditing}
        />
        <MemberPortalChapters initialData={current.chapters} />
        <CustomFieldsSection
          fields={current.custom_fields}
          draft={workingDraft}
          editing={editing === 'custom_fields'}
          errors={errors}
          onDraft={setDraft}
          onEdit={() => beginEditing('custom_fields')}
          onSave={save}
          onCancel={cancelEditing}
          pending={update.isPending}
        />
      </Card>
    </div>
  )
}

function ContactSection({
  current,
  draft,
  editing,
  errors,
  onDraft,
  onEdit,
  onSave,
  onCancel,
  pending,
}: {
  current: MemberPortalProfile
  draft: ProfileDraft
  editing: boolean
  errors: ProfileErrors
  onDraft: React.Dispatch<React.SetStateAction<ProfileDraft | null>>
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  pending: boolean
}) {
  return (
    <ProfileSection title="Contact details" editing={editing} onEdit={onEdit}>
      <ProfileFieldRow label="Name" value={current.name} editing={editing} error={errors.name}>
        <Input
          id="profile-name"
          value={draft.name}
          onChange={(event) => onDraft({ ...draft, name: event.target.value })}
        />
      </ProfileFieldRow>
      {(current.phone !== undefined || editing) && (
        <ProfileFieldRow label="Phone" value={current.phone} editing={editing} error={errors.phone}>
          <Input
            id="profile-phone"
            value={draft.phone}
            onChange={(event) => onDraft({ ...draft, phone: event.target.value })}
          />
        </ProfileFieldRow>
      )}
      <MemberPortalPostalFields
        current={current}
        draft={draft}
        editing={editing}
        onDraft={onDraft}
        errors={errors}
      />
      {editing && <SectionActions onSave={onSave} onCancel={onCancel} pending={pending} />}
    </ProfileSection>
  )
}

function CustomFieldsSection({
  fields,
  draft,
  editing,
  errors,
  onDraft,
  onEdit,
  onSave,
  onCancel,
  pending,
}: {
  fields: ProfileField[]
  draft: ProfileDraft
  editing: boolean
  errors: ProfileErrors
  onDraft: React.Dispatch<React.SetStateAction<ProfileDraft | null>>
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  pending: boolean
}) {
  if (fields.length === 0) return null
  return (
    <ProfileSection title="Custom fields" editing={editing} onEdit={onEdit}>
      {fields.map((field) => (
        <CustomFieldRow
          key={field.label}
          field={field}
          draft={draft}
          editing={editing}
          errors={errors}
          onDraft={onDraft}
        />
      ))}
      {editing && <SectionActions onSave={onSave} onCancel={onCancel} pending={pending} />}
    </ProfileSection>
  )
}

function CustomFieldRow({
  field,
  draft,
  editing,
  errors,
  onDraft,
}: {
  field: ProfileField
  draft: ProfileDraft
  editing: boolean
  errors: ProfileErrors
  onDraft: React.Dispatch<React.SetStateAction<ProfileDraft | null>>
}) {
  const fieldKey = String(field.id ?? field.label)
  const value = draft.customFields[fieldKey] ?? ''
  const fieldError = errors[`custom_field_${fieldKey}`] ?? errors[field.label]
  const change = (next: string) => setCustomField(onDraft, draft, fieldKey, next)
  return (
    <ProfileFieldRow
      label={field.label}
      value={field.value}
      file={field.type === 'file'}
      editing={editing && field.field_access === 'member_edit'}
      error={fieldError}
    >
      {field.type === 'multiline_text' ? (
        <Textarea
          id={`profile-field-${fieldKey}`}
          value={String(value)}
          onChange={(event) => change(event.target.value)}
        />
      ) : (
        <Input
          id={`profile-field-${fieldKey}`}
          type={field.type === 'number' ? 'number' : 'text'}
          value={String(value)}
          onChange={(event) => change(event.target.value)}
        />
      )}
    </ProfileFieldRow>
  )
}

function ProfileSection({
  title,
  editing,
  onEdit,
  children,
}: {
  title: string
  editing: boolean
  onEdit: () => void
  children: React.ReactNode
}) {
  return (
    <section className="border-t border-border first:border-t-0">
      <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
        <h2 className="text-body font-medium text-foreground">{title}</h2>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Edit ${title}`}
            onClick={onEdit}
          >
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      <div className="px-5 pb-5 sm:px-6 sm:pb-6">{children}</div>
    </section>
  )
}

function ProfileFieldRow({
  label,
  value,
  file = false,
  editing,
  error,
  children,
}: {
  label: string
  value: string | boolean | undefined
  file?: boolean
  editing: boolean
  error?: string[]
  children?: React.ReactNode
}) {
  return (
    <div className="py-2.5">
      <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
        <Label
          htmlFor={editing ? `profile-${label.toLowerCase().replaceAll(' ', '-')}` : undefined}
          className="w-full shrink-0 text-body-sm font-normal text-muted-foreground sm:w-40"
        >
          {label}
        </Label>
        <div className="min-w-0 flex-1 text-body-sm text-foreground">
          {editing ? (
            children
          ) : (
            <span className="flex items-center gap-2 break-words">
              {file && (
                <FileText
                  className="size-4 shrink-0 text-muted-foreground"
                  data-testid="profile-file-icon"
                  aria-hidden="true"
                />
              )}
              {profileValue(value)}
            </span>
          )}
        </div>
      </div>
      {error?.map((message) => (
        <p key={message} className="mt-1 text-body-sm text-destructive" role="alert">
          {message}
        </p>
      ))}
    </div>
  )
}

function SectionActions({
  onSave,
  onCancel,
  pending,
}: {
  onSave: () => void
  onCancel: () => void
  pending: boolean
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-4">
      <Button type="button" size="sm" onClick={onSave} disabled={pending}>
        {pending ? 'Saving...' : 'Save'}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
    </div>
  )
}
